import { cn } from '@workspace/ui/lib/utils'
import type { ReactNode } from 'react'

/**
 * Placeholder tokens that outreach message templates carry inline. The backend
 * substitutes them per-contact at HeyReach push time; the studio review UI
 * resolves them for a live preview. Keep in sync with the backend token set in
 * apps/backend/src/constants/heyreach.ts (`renderOutreachTemplate`).
 */
export const OUTREACH_TOKENS = [
  '[FirstName]',
  '[FundName]',
  '[FundFocus]',
  '[FundSector]',
  '[Link]',
] as const

type TokenResolve = {
  firstName?: string
  fundName?: string
  /** Fund sector/stage phrase for the [FundFocus] token. */
  fundFocus?: string
  /** Sector-only phrase for the [FundSector] token (no stage word). */
  fundSector?: string
  /** Display text for the [Link] token in the preview (e.g. a short profile URL). */
  link?: string
}

/**
 * Derive the fund-focus tokens for a preview from an investor profile. Mirrors
 * the backend `formatFundFocus` / `formatFundSector`: [FundFocus] is the stage
 * word plus up to two sectors, [FundSector] the sectors alone. Undefined when
 * there is no data, so the token renders as its own placeholder.
 */
export function fundTokensFromProfile(
  stages: string[] | null | undefined,
  sectors: string[] | null | undefined
): Pick<TokenResolve, 'fundFocus' | 'fundSector'> {
  const fundStage = (stages?.[0] ?? '').toLowerCase()
  const fundSector = (sectors ?? []).slice(0, 2).join(' and ')
  return {
    fundFocus: [fundStage, fundSector].filter(Boolean).join(' ') || undefined,
    fundSector: fundSector || undefined,
  }
}

// `[Ff]irstName` matches both the current [FirstName] token and any legacy [firstName].
const TOKEN_SPLIT_RE = /(\[[Ff]irstName\]|\[FundName\]|\[FundFocus\]|\[FundSector\]|\[Link\])/g

function isToken(part: string): boolean {
  return /^\[(?:[Ff]irstName|FundName|FundFocus|FundSector|Link)\]$/.test(part)
}

function resolvedValue(token: string, resolve: TokenResolve): string {
  const t = token.toLowerCase()
  if (t === '[firstname]') return resolve.firstName || 'there'
  if (t === '[fundname]') return resolve.fundName || '[FundName]'
  if (t === '[fundfocus]') return resolve.fundFocus || '[FundFocus]'
  if (t === '[fundsector]') return resolve.fundSector || '[FundSector]'
  if (t === '[link]') return resolve.link || 'your-seedforge-profile.link'
  return token
}

/**
 * Render a template string with its tokens substituted. Resolved name/fund
 * tokens read as plain text (so the preview looks like a real message); only
 * the `[Link]` token stays highlighted to flag the per-contact tracking link.
 */
export function renderTemplatePreview(text: string, resolve: TokenResolve): ReactNode {
  return text.split(TOKEN_SPLIT_RE).map((part, i) => {
    if (part.toLowerCase() === '[link]') {
      return (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: static split, index is stable
          key={i}
          className={cn('rounded-[3px] px-1 py-0.5 font-medium', 'bg-primary/15 text-primary')}
        >
          {resolvedValue(part, resolve)}
        </span>
      )
    }
    if (isToken(part)) {
      // biome-ignore lint/suspicious/noArrayIndexKey: static split, index is stable
      return <span key={i}>{resolvedValue(part, resolve)}</span>
    }
    // biome-ignore lint/suspicious/noArrayIndexKey: static split, index is stable
    return <span key={i}>{part}</span>
  })
}

/** Message touch in the three-message outreach sequence. */
export type OutreachMessageField = 'msg1' | 'msg2' | 'msg3'

// Link frame the new-format templates use before the [Link] token. Legacy
// messages get this appended in the preview so they read like a new template.
const LEGACY_LINK_FRAME = 'Our full picture in one link:'

/**
 * First Langfuse prompt version at which each message switched to the SF-121-v2
 * self-contained token format. MUST stay in sync with the backend
 * `OUTREACH_FULL_TEMPLATE_MIN_VERSION` (apps/backend/src/constants/heyreach.ts) —
 * the studio preview and the backend push decide legacy-vs-v2 the same way.
 */
export const OUTREACH_FULL_TEMPLATE_MIN_VERSION = {
  investor: { msg1: 12, msg2: 10, msg3: 7 },
  portfolio: { msg1: 3, msg2: 3, msg3: 3 },
} as const

export type OutreachTrack = 'investor' | 'portfolio'

/**
 * Legacy detection by prompt VERSION — mirrors the backend
 * `isLegacyOutreachMessage`: numeric version < cutoff → legacy; >= cutoff → v2;
 * non-numeric markers ("custom", "fallback") and absent versions → v2 (after the
 * backfill, every legacy row carries a numeric version below its cutoff, so a
 * non-numeric marker only ever comes from the v2 path).
 */
export function isLegacyOutreachByVersion(
  promptVersion: string | null | undefined,
  minFullTemplateVersion: number
): boolean {
  const parsed = Number.parseInt((promptVersion ?? '').trim(), 10)
  if (Number.isNaN(parsed)) return false
  return parsed < minFullTemplateVersion
}

/** Version-based legacy check for a specific touch on a specific track. */
export function isLegacyOutreachField(
  field: OutreachMessageField,
  promptVersion: string | null | undefined,
  track: OutreachTrack
): boolean {
  return isLegacyOutreachByVersion(promptVersion, OUTREACH_FULL_TEMPLATE_MIN_VERSION[track][field])
}

/**
 * Proxy that decorates a legacy (tokenless) message into the new-template token
 * shape for the founder preview, so old messages render consistently with new
 * ones. Mirrors what the backend's legacy `assemble*` path composes at push:
 * prepend the `[FirstName]` greeting, fold in the separate legacy CTA for msg2
 * (before the link), then append the link frame + `[Link]` for the touches that
 * carry a link (msg1/msg2). Display-only — the stored/editable draft is untouched.
 */
export function decorateLegacyOutreachPreview(
  field: OutreachMessageField,
  text: string,
  cta?: string | null
): string {
  const body = (text ?? '').trim()
  if (!body) return body
  const greeted = `Hey [FirstName], ${body}`
  if (field === 'msg3') return greeted
  const trimmedCta = (cta ?? '').trim()
  const withCta = field === 'msg2' && trimmedCta ? `${greeted} ${trimmedCta}` : greeted
  return `${withCta} ${LEGACY_LINK_FRAME} [Link]`
}

/** Plain-text substitution (no highlighting) — for char-count/preview strings. */
export function substituteTemplateTokens(text: string, resolve: TokenResolve): string {
  return text
    .replace(/\[firstName\]/gi, resolve.firstName || 'there')
    .replaceAll('[FundName]', resolve.fundName || '[FundName]')
    .replaceAll('[FundFocus]', resolve.fundFocus || '[FundFocus]')
    .replaceAll('[FundSector]', resolve.fundSector || '[FundSector]')
    .replaceAll('[Link]', resolve.link || 'your-seedforge-profile.link')
}
